// roofStructure.ts — ACI / SJI roof framing calculations for tilt-up buildings
//
// References:
//   ACI 318-19 Chapter 17 — Anchoring to Concrete (ledger embed depth, anchor bolt design)
//   ACI 551.2R — Design Guide for Tilt-Up Concrete Panels
//   SJI 45th Edition — Standard Specifications for Open Web Steel Joists (K/LH/DLH series)
//   ASCE 7-22 — Minimum Design Loads (roof live + dead loads)
//
// Conventions:
//   - All dimensions in inches unless noted
//   - Loads in psf (pounds per square foot)
//   - Joists span the SHORT dimension of the roof (most efficient)

import type { RoofStructure, LedgerConnection } from '../types/panelvar';

// ── Load assumptions (ASCE 7-22 typical warehouse/industrial) ──────────────
const ROOF_DEAD_LOAD = 15;    // psf — steel deck + insulation + roofing
const ROOF_LIVE_LOAD = 20;    // psf — ASCE 7-22 Table 4.3-1 (ordinary flat roof)
const TOTAL_ROOF_LOAD = ROOF_DEAD_LOAD + ROOF_LIVE_LOAD; // 35 psf

// ── SJI K-series joist selection table (simplified) ────────────────────────
// Span (ft) → { designation, depth (in), safe load capacity (plf) at that span }
// Based on SJI K-series load tables for 35 psf total load
interface JoistEntry {
  designation: string;
  depth: number;       // inches
  maxSpanFt: number;   // max span in feet
}

const K_SERIES_JOISTS: JoistEntry[] = [
  { designation: '8K1',  depth: 8,  maxSpanFt: 18 },
  { designation: '10K1', depth: 10, maxSpanFt: 22 },
  { designation: '12K1', depth: 12, maxSpanFt: 24 },
  { designation: '14K1', depth: 14, maxSpanFt: 28 },
  { designation: '16K2', depth: 16, maxSpanFt: 32 },
  { designation: '18K3', depth: 18, maxSpanFt: 36 },
  { designation: '20K4', depth: 20, maxSpanFt: 40 },
  { designation: '22K5', depth: 22, maxSpanFt: 44 },
  { designation: '24K5', depth: 24, maxSpanFt: 48 },
  { designation: '26K5', depth: 26, maxSpanFt: 50 },
  { designation: '28K7', depth: 28, maxSpanFt: 54 },
  { designation: '30K7', depth: 30, maxSpanFt: 60 },
];

// LH-series for longer spans
const LH_SERIES_JOISTS: JoistEntry[] = [
  { designation: '32LH06', depth: 32, maxSpanFt: 64 },
  { designation: '36LH07', depth: 36, maxSpanFt: 72 },
  { designation: '40LH08', depth: 40, maxSpanFt: 80 },
  { designation: '44LH09', depth: 44, maxSpanFt: 88 },
  { designation: '48LH10', depth: 48, maxSpanFt: 96 },
];

/**
 * Select the most economical SJI joist for a given clear span.
 */
function selectJoist(spanInches: number): { designation: string; depth: number } {
  const spanFt = spanInches / 12;

  for (const j of K_SERIES_JOISTS) {
    if (j.maxSpanFt >= spanFt) {
      return { designation: j.designation, depth: j.depth };
    }
  }
  for (const j of LH_SERIES_JOISTS) {
    if (j.maxSpanFt >= spanFt) {
      return { designation: j.designation, depth: j.depth };
    }
  }
  // Exceeds table — return largest available
  const last = LH_SERIES_JOISTS[LH_SERIES_JOISTS.length - 1];
  return { designation: last.designation, depth: last.depth };
}

/**
 * Compute joist spacing based on span and load.
 * Standard OWSJ spacing for tilt-up: 4'-0" to 6'-0" o.c.
 * Shorter spans can go wider; longer spans need tighter spacing.
 */
function computeJoistSpacing(spanInches: number): number {
  const spanFt = spanInches / 12;
  if (spanFt <= 30) return 72;  // 6'-0" o.c.
  if (spanFt <= 45) return 60;  // 5'-0" o.c.
  return 48;                     // 4'-0" o.c.
}

/**
 * Determine ledger connection per ACI 318 Ch.17 and ACI 551.2R.
 * Ledger = steel angle bolted to interior face of tilt-up panel.
 *
 * ACI 318-19 §17.4.2: Minimum embed depth = 4× bolt diameter for
 * cast-in anchors in normal-weight concrete (f'c ≥ 3000 psi).
 */
function computeLedger(spanInches: number): LedgerConnection {
  const spanFt = spanInches / 12;

  // Angle size — larger for longer spans (heavier reactions)
  let angleSize: string;
  let boltDia: number;
  if (spanFt <= 30) {
    angleSize = 'L4x4x5/16';
    boltDia = 0.625;          // 5/8" anchor bolt
  } else if (spanFt <= 50) {
    angleSize = 'L4x4x3/8';
    boltDia = 0.75;           // 3/4" anchor bolt
  } else {
    angleSize = 'L6x4x3/8';
    boltDia = 0.75;
  }

  // Anchor bolt spacing — tighter for heavier loads
  // Tributary width × total load → reaction per foot of ledger
  // Space bolts so each anchor carries ≤ 5 kips (typical cast-in capacity)
  const tributaryFt = spanFt / 2; // each ledger carries half the span
  const loadPerFt = tributaryFt * TOTAL_ROOF_LOAD; // plf
  const boltCapacity = 5000; // lbs per anchor (conservative for 3/4" cast-in, f'c=4000)
  const maxSpacing = Math.floor(boltCapacity / loadPerFt);
  const spacing = Math.min(Math.max(maxSpacing, 24), 48) ; // clamp 24"–48" o.c.

  // Embed depth: ACI 318-19 §17.4.2 — minimum 4× bolt diameter,
  // but practical minimum is 4" for constructability
  const embedDepth = Math.max(4 * boltDia, 4);

  return {
    angleSize,
    anchorBoltDia: boltDia,
    anchorBoltSpacing: spacing,
    embedDepth: Math.round(embedDepth * 100) / 100,
  };
}

/**
 * Select steel roof deck type.
 * Standard for tilt-up: 1.5" Type B (wide-rib), gauge based on joist spacing.
 */
function selectDeck(joistSpacing: number): string {
  if (joistSpacing <= 48) return '1.5B22';  // 22 gauge
  if (joistSpacing <= 60) return '1.5B20';  // 20 gauge
  return '1.5B18';                           // 18 gauge
}

/**
 * Compute complete roof structure from the inset outline dimensions.
 *
 * @param outlinePoints - Inset polygon points (from computeRoofOutline)
 * @returns RoofStructure with joist selection, spacing, ledger, and deck
 */
export function computeRoofStructure(outlinePoints: { x: number; y: number }[]): RoofStructure {
  if (outlinePoints.length < 3) {
    // Degenerate — return sensible defaults
    return defaultRoofStructure();
  }

  // Compute bounding box of inset outline to determine spans
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of outlinePoints) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const spanX = maxX - minX;
  const spanY = maxY - minY;

  // Joists span the short dimension (most economical)
  const shortSpan = Math.min(spanX, spanY);
  const spanDirection: 'short' | 'long' = spanX <= spanY ? 'short' : 'long';

  const joist = selectJoist(shortSpan);
  const spacing = computeJoistSpacing(shortSpan);
  const deck = selectDeck(spacing);
  const ledger = computeLedger(shortSpan);

  return {
    framingType: 'owsj',
    framingSpacing: spacing,
    framingDepth: joist.depth,
    span: shortSpan,
    spanDirection,
    deckType: deck,
    ledger,
  };
}

/** Sensible default structure for fallback */
function defaultRoofStructure(): RoofStructure {
  return {
    framingType: 'owsj',
    framingSpacing: 60,
    framingDepth: 18,
    span: 360,
    spanDirection: 'short',
    deckType: '1.5B22',
    ledger: {
      angleSize: 'L4x4x3/8',
      anchorBoltDia: 0.75,
      anchorBoltSpacing: 36,
      embedDepth: 4,
    },
  };
}

/** Format a roof structure summary for display */
export function formatRoofStructureSummary(rs: RoofStructure): string[] {
  const spanFt = Math.round(rs.span / 12);
  const spacingIn = rs.framingSpacing;
  const lines: string[] = [];
  lines.push(`Framing: Open Web Steel Joists @ ${spacingIn}" o.c.`);
  lines.push(`Clear span: ${spanFt}'-0" (${rs.spanDirection} direction)`);
  lines.push(`Joist depth: ${rs.framingDepth}"`);
  lines.push(`Deck: ${rs.deckType} steel deck`);
  lines.push(`Ledger: ${rs.ledger.angleSize} w/ ${rs.ledger.anchorBoltDia}" anchor bolts @ ${rs.ledger.anchorBoltSpacing}" o.c.`);
  lines.push(`Embed depth: ${rs.ledger.embedDepth}" (ACI 318-19 §17.4.2)`);
  lines.push(`Design load: ${TOTAL_ROOF_LOAD} psf (${ROOF_DEAD_LOAD} DL + ${ROOF_LIVE_LOAD} LL)`);
  return lines;
}
